/**
 * Config Tab
 *
 * Store configuration with per-section independent toggles.
 * Each section (pricing, alerts, schedule) can independently use
 * system defaults or custom values.
 *
 * @module StoreCommandCenter/components/tabs
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  Collapse,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Notifications as AlertIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  Edit as EditIcon,
  Check as SaveIcon,
  Close as CancelIcon,
  LocalShipping as DeliveryIcon,
  ShoppingCart as CartIcon,
  Inventory as OperationsIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Schedule as ClockIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import StoreLocationForm from '../../../../components/StoreLocation/StoreLocationForm';
import type { StoreLocationView } from '../../../../components/StoreLocation/storeLocationApi';
import type { StoreCommandData } from '../../hooks/useCommandCenter';
import { SmartScheduleEditor, type ScheduleData } from '../ui/SmartScheduleEditor';
import { useAdminTheme, type AdminColorTokens } from '../../../../admin-theme';
import { SHOW_SLA } from '../../../../config/features';
import Logger from '../../../../utils/Logger';
import config from '../../../../config';
import { getValidToken } from '../../../../services/tokenRefreshService';

// ============================================================================
// Types
// ============================================================================

interface ConfigTabProps {
  store: StoreCommandData;
}

interface AlertSettings {
  alertsEnabled: boolean;
  pickingSlaMinutes: number;
  packingSlaMinutes: number;
  pickupSlaMinutes: number;
}

interface UberVerificationSettings {
  signature?: { enabled: boolean; collect_signer_name?: boolean; collect_signer_relationship?: boolean };
  picture?: boolean;
  pincode?: { enabled: boolean };
  verificationMethod?: string;
}

interface StoreSettingsData {
  isActive: boolean;
  deliveryCharge: number | null;
  freeDeliveryAmount: number | null;
  minimumCartCheckout: number | null;
  serviceFee: number | null;
  pickupNotes: string;
  enablePickingFlow: boolean;
  alertSettings: AlertSettings | null;
  /** How this store trades on an enabled public holiday. Defaults to closed. */
  holidayRule: 'closed' | 'normal' | 'custom';
  holidayOpenTime: string | null;
  holidayCloseTime: string | null;
}

interface DefaultsData {
  deliveryCharge: number;
  freeDeliveryAmount: number | null;
  minimumCartCheckout: number;
  serviceFee: number;
  uberDropoffVerification: UberVerificationSettings | null;
  uberReturnVerification: UberVerificationSettings | null;
  alertSettings: AlertSettings;
}

// ============================================================================
// Sub-components
// ============================================================================

function PlateToggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  colors,
  status = false,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  colors: AdminColorTokens;
  /** On/off status (Store is Active): green when on, red when off. Default = red accent when on. */
  status?: boolean;
}) {
  const accent = status ? (checked ? colors.success : colors.error) : colors.error;
  const lit = status || checked; // a status toggle is coloured both ways
  return (
    <Box
      onClick={() => !disabled && onChange(!checked)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: lit ? `${accent}08` : colors.bgDeepest,
        border: `1px solid ${lit ? `${accent}40` : colors.border}`,
        boxShadow: lit ? `0 0 15px ${accent}15` : 'none',
        transition: 'all 0.3s',
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled ? {} : {
          bgcolor: lit ? `${accent}12` : colors.bgElevated,
          borderColor: lit ? `${accent}60` : colors.textMuted,
        },
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ color: checked ? colors.textPrimary : colors.textSecondary }}>
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: accent,
            '& + .MuiSwitch-track': { bgcolor: `${accent}60` },
          },
          '& .MuiSwitch-track': { bgcolor: status && !checked ? `${colors.error}60` : colors.border },
        }}
      />
    </Box>
  );
}

interface SectionCardProps {
  icon: React.ReactElement;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  accentColor?: string;
  badge?: React.ReactNode;
  colors: AdminColorTokens;
}

function SectionCard({ icon, title, expanded, onToggle, children, accentColor, badge, colors }: SectionCardProps) {
  const accent = accentColor || colors.info;
  return (
    <Paper
      sx={{
        bgcolor: colors.bgCard,
        borderRadius: 2,
        border: `1px solid ${expanded ? `${accent}40` : colors.border}`,
        overflow: 'hidden',
        mb: 2,
        transition: 'all 0.3s',
        boxShadow: expanded ? `0 0 20px ${accent}10` : 'none',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          bgcolor: expanded ? `${accent}08` : 'transparent',
          transition: 'all 0.2s',
          '&:hover': { bgcolor: `${accent}12` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}15`,
              border: `1px solid ${accent}30`,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 20, color: accent } } as any)}
          </Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: colors.textPrimary }}>
            {title}
          </Typography>
          {badge}
        </Box>
        {expanded ? (
          <CollapseIcon sx={{ color: colors.textMuted }} />
        ) : (
          <ExpandIcon sx={{ color: colors.textMuted }} />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2.5, borderTop: `1px solid ${colors.border}` }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

/** Compact badge for displaying a setting value */
function SettingBadge({
  icon,
  label,
  value,
  color,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  colors: AdminColorTokens;
}) {
  const badgeColor = color || colors.info;
  return (
    <Chip
      icon={<>{icon}</>}
      label={<><span style={{ color: colors.textMuted, marginRight: 4 }}>{label}:</span> {value}</>}
      size="small"
      sx={{
        height: 28,
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: `${badgeColor}10`,
        color: colors.textPrimary,
        border: `1px solid ${badgeColor}30`,
        '& .MuiChip-icon': { color: badgeColor, fontSize: 16 },
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

/**
 * A Store Information value with a pencil, like Phone Number: click to edit, tick to save,
 * cross to cancel. onSave throws to keep the field open (the error shows in the snackbar).
 */
function InlineEditField({
  icon,
  label,
  value,
  emptyText,
  placeholder,
  helper,
  maxLength,
  onSave,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  emptyText: string;
  placeholder?: string;
  helper?: string;
  maxLength: number;
  onSave: (next: string) => Promise<void>;
  colors: AdminColorTokens;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch {
      /* stays open; the caller reported the error */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>{label}</Typography>
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <TextField
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              placeholder={placeholder}
              disabled={saving}
              autoFocus
              inputProps={{ maxLength }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.bgCard,
                  color: colors.textPrimary,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { py: 0.75, px: 1.5, fontSize: '0.875rem' },
              }}
            />
            <IconButton size="small" onClick={save} disabled={saving} sx={{ color: colors.success }}>
              {saving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ color: colors.error }}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: value ? colors.textPrimary : colors.textMuted }}>
              {value || emptyText}
            </Typography>
            <IconButton
              size="small"
              aria-label={`Edit ${label}`}
              onClick={() => { setDraft(value); setEditing(true); }}
              sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.info } }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
        {helper && (
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.68rem' }}>{helper}</Typography>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ConfigTab({ store }: ConfigTabProps) {
  const { mode, colors } = useAdminTheme();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Per-section custom toggles
  const [useCustomPricing, setUseCustomPricing] = useState(false);
  const [useCustomAlerts, setUseCustomAlerts] = useState(false);

  // Section expansion states (for custom mode)
  // Every section starts collapsed; Store Information above them is always open (Jason, 1 Nov 2026)
  const [pricingExpanded, setPricingExpanded] = useState(false);
  const [operationsExpanded, setOperationsExpanded] = useState(false);
  const [savingPickupNotes, setSavingPickupNotes] = useState(false);
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  const [locationView, setLocationView] = useState<StoreLocationView | null>(null);

  // Store settings
  const [settings, setSettings] = useState<StoreSettingsData>({
    isActive: true,
    deliveryCharge: null,
    freeDeliveryAmount: null,
    minimumCartCheckout: null,
    serviceFee: null,
    pickupNotes: '',
    enablePickingFlow: false,
    alertSettings: null,
    holidayRule: 'closed',
    holidayOpenTime: null,
    holidayCloseTime: null,
  });

  // System defaults
  const [defaults, setDefaults] = useState<DefaultsData>({
    deliveryCharge: 0,
    freeDeliveryAmount: null,
    minimumCartCheckout: 50,
    serviceFee: 19,
    uberDropoffVerification: null,
    uberReturnVerification: null,
    alertSettings: { alertsEnabled: true, pickingSlaMinutes: 10, packingSlaMinutes: 15, pickupSlaMinutes: 30 },
  });

  // Phone editing
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState(store.phoneNumber || '');
  const [savingPhone, setSavingPhone] = useState(false);

  // Store name (a rename moves its Radar zones and stock rows too) and the two display names
  const [storeName, setStoreName] = useState(store.name);
  const [inAppDisplayName, setInAppDisplayName] = useState(store.inAppDisplayName || '');
  const [courierDisplayName, setCourierDisplayName] = useState(store.courierDisplayName || '');
  useEffect(() => {
    setStoreName(store.name);
    setInAppDisplayName(store.inAppDisplayName || '');
    setCourierDisplayName(store.courierDisplayName || '');
  }, [store.id, store.name, store.inAppDisplayName, store.courierDisplayName]);

  // Schedule state
  const [schedule, setSchedule] = useState<ScheduleData>({});
  const [globalSchedule, setGlobalSchedule] = useState<ScheduleData>({});
  const [useGlobalSchedule, setUseGlobalSchedule] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Initial state tracking for change detection
  const [initialSettings, setInitialSettings] = useState<StoreSettingsData | null>(null);
  const [initialUseCustomPricing, setInitialUseCustomPricing] = useState(false);
  const [initialUseCustomAlerts, setInitialUseCustomAlerts] = useState(false);
  const [initialUseGlobalSchedule, setInitialUseGlobalSchedule] = useState(true);
  const [initialSchedule, setInitialSchedule] = useState<ScheduleData>({});

  // Fetch store settings on mount
  useEffect(() => {
    fetchSettings();
    fetchScheduleData();
  }, [store.id]);

  // Derive per-section custom toggles once after both settings and schedule finish loading
  const firstLoadDone = React.useRef(false);
  useEffect(() => {
    if (!loading && !scheduleLoading && !firstLoadDone.current) {
      firstLoadDone.current = true;
      const customPricing = settings.deliveryCharge !== null || settings.freeDeliveryAmount !== null || settings.minimumCartCheckout !== null || settings.serviceFee !== null;
      const customAlerts = settings.alertSettings !== null;
      setUseCustomPricing(customPricing);
      setUseCustomAlerts(customAlerts);
      setInitialUseCustomPricing(customPricing);
      setInitialUseCustomAlerts(customAlerts);
    }
  }, [loading, scheduleLoading]);

  const fetchSettings = async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok || !data.store) {
        throw new Error('Failed to fetch settings');
      }

      // Set defaults
      setDefaults({
        deliveryCharge: data.defaults.deliveryCharge ?? 0,
        freeDeliveryAmount: data.defaults.freeDeliveryAmount ?? null,
        minimumCartCheckout: data.defaults.minimumCartCheckout ?? 50,
        serviceFee: data.defaults.serviceFee ?? 19,
        uberDropoffVerification: data.defaults.uberDropoffVerification ?? null,
        uberReturnVerification: data.defaults.uberReturnVerification ?? null,
        alertSettings: data.defaults.alertSettings ?? { alertsEnabled: true, pickingSlaMinutes: 10, packingSlaMinutes: 15, pickupSlaMinutes: 30 },
      });

      // Set store settings
      const storeSettings = {
        isActive: data.store.isActive ?? true,
        deliveryCharge: data.store.deliveryCharge,
        freeDeliveryAmount: data.store.freeDeliveryAmount,
        minimumCartCheckout: data.store.minimumCartCheckout,
        serviceFee: data.store.serviceFee,
        pickupNotes: data.store.pickupNotes || '',
        enablePickingFlow: data.store.enablePickingFlow ?? false,
        alertSettings: data.store.alertSettings,
        holidayRule: data.store.holidayRule ?? 'closed',
        holidayOpenTime: data.store.holidayOpenTime ?? null,
        holidayCloseTime: data.store.holidayCloseTime ?? null,
      };
      setSettings(storeSettings);
      setInitialSettings(storeSettings);
    } catch (err: any) {
      Logger.error('Failed to fetch store settings:', err);
      setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Transform backend working hours and early cutoffs to SmartScheduleEditor format
   */
  const transformToScheduleData = (
    workingHours: Array<{ dayOfWeek: number; openTime: string; closeTime: string }>,
    earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }>
  ): ScheduleData => {
    const scheduleData: ScheduleData = {};
    const cutoffMap = new Map(earlyCutoffs.map(ec => [ec.dayOfWeek, ec.cutoffTime]));
    const startTimeMap = new Map(earlyCutoffs.map(ec => [ec.dayOfWeek, ec.startTime]));

    for (let day = 0; day < 7; day++) {
      scheduleData[day] = {
        openTime: '',
        closeTime: '',
        liquorCutoff: cutoffMap.get(day)?.substring(0, 5) || null,
        liquorStartTime: startTimeMap.get(day)?.substring(0, 5) || null,
      };
    }

    for (const wh of workingHours) {
      if (scheduleData[wh.dayOfWeek] && !scheduleData[wh.dayOfWeek].openTime) {
        scheduleData[wh.dayOfWeek].openTime = wh.openTime?.substring(0, 5) || '';
        scheduleData[wh.dayOfWeek].closeTime = wh.closeTime?.substring(0, 5) || '';
      }
    }

    return scheduleData;
  };

  /**
   * Transform SmartScheduleEditor format back to backend format
   */
  const transformFromScheduleData = (scheduleData: ScheduleData): {
    workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }>;
    earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }>;
  } => {
    const workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }> = [];
    const earlyCutoffs: Array<{ dayOfWeek: number; cutoffTime: string; startTime?: string }> = [];

    for (let day = 0; day < 7; day++) {
      const daySchedule = scheduleData[day];
      if (daySchedule) {
        if (daySchedule.openTime && daySchedule.closeTime) {
          workingHours.push({
            dayOfWeek: day,
            slotNumber: 1,
            openTime: daySchedule.openTime.length === 5 ? `${daySchedule.openTime}:00` : daySchedule.openTime,
            closeTime: daySchedule.closeTime.length === 5 ? `${daySchedule.closeTime}:00` : daySchedule.closeTime,
          });
        }
        if (daySchedule.liquorCutoff || daySchedule.liquorStartTime) {
          const entry: { dayOfWeek: number; cutoffTime: string; startTime?: string } = {
            dayOfWeek: day,
            cutoffTime: daySchedule.liquorCutoff
              ? (daySchedule.liquorCutoff.length === 5 ? `${daySchedule.liquorCutoff}:00` : daySchedule.liquorCutoff)
              : '',
          };
          if (daySchedule.liquorStartTime) {
            entry.startTime = daySchedule.liquorStartTime.length === 5
              ? `${daySchedule.liquorStartTime}:00`
              : daySchedule.liquorStartTime;
          }
          earlyCutoffs.push(entry);
        }
      }
    }

    return { workingHours, earlyCutoffs };
  };

  const fetchScheduleData = useCallback(async () => {
    setScheduleLoading(true);

    try {
      const token = await getValidToken();
      const [hoursRes, cutoffsRes, defaultsRes] = await Promise.all([
        fetch(`${config.API_URL}/admin/stores/${store.id}/working-hours`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${config.API_URL}/admin/stores/${store.id}/early-cutoffs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${config.API_URL}/admin/stores/defaults`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!hoursRes.ok || !cutoffsRes.ok || !defaultsRes.ok) {
        throw new Error('Failed to fetch schedule data');
      }

      const [storeHours, storeCutoffs, systemDefaults] = await Promise.all([
        hoursRes.json(),
        cutoffsRes.json(),
        defaultsRes.json(),
      ]);

      const globalSched = transformToScheduleData(
        systemDefaults.defaultWorkingHours || [],
        systemDefaults.defaultEarlyCutoffs || []
      );
      setGlobalSchedule(globalSched);

      const storeSched = transformToScheduleData(storeHours || [], storeCutoffs || []);
      setSchedule(storeSched);

      const hasStoreHours = storeHours && storeHours.length > 0;
      setUseGlobalSchedule(!hasStoreHours);
      setInitialUseGlobalSchedule(!hasStoreHours);
      setInitialSchedule(storeSched);
    } catch (err: any) {
      Logger.error('Failed to fetch schedule data:', err);
      setSnackbar({ open: true, message: 'Failed to load schedule', severity: 'error' });
    } finally {
      setScheduleLoading(false);
    }
  }, [store.id]);

  const handleSaveAll = async () => {
    setSaving(true);

    try {
      const token = await getValidToken();
      const body = {
        isActive: settings.isActive,
        deliveryCharge: useCustomPricing ? settings.deliveryCharge : null,
        freeDeliveryAmount: useCustomPricing ? settings.freeDeliveryAmount : null,
        minimumCartCheckout: useCustomPricing ? settings.minimumCartCheckout : null,
        serviceFee: useCustomPricing ? settings.serviceFee : null,
        pickupNotes: settings.pickupNotes.trim() || null,
        enablePickingFlow: settings.enablePickingFlow,
        alertSettings: useCustomAlerts ? settings.alertSettings : null,
        holidayRule: settings.holidayRule,
        // Only meaningful on 'custom'; the API clears them otherwise.
        holidayOpenTime: settings.holidayRule === 'custom' ? settings.holidayOpenTime : null,
        holidayCloseTime: settings.holidayRule === 'custom' ? settings.holidayCloseTime : null,
      };

      const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save');
      }

      // Also save schedule if not using global schedule
      if (!useGlobalSchedule) {
        await handleScheduleSave();
      } else if (useGlobalSchedule) {
        // Clear custom schedule
        await Promise.all([
          fetch(`${config.API_URL}/admin/stores/${store.id}/working-hours`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ workingHours: [] }),
          }),
          fetch(`${config.API_URL}/admin/stores/${store.id}/early-cutoffs`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ earlyCutoffs: [] }),
          }),
        ]);
      }

      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });

      // Refresh to get actual state and reset initial values
      setInitialSettings(null);
      firstLoadDone.current = false; // Allow per-section toggles to re-derive after refetch
      await fetchSettings();
      await fetchScheduleData();
    } catch (err: any) {
      Logger.error('Failed to save:', err);
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleSave = async () => {
    setSavingSchedule(true);
    try {
      const token = await getValidToken();
      const { workingHours, earlyCutoffs } = transformFromScheduleData(schedule);

      await Promise.all([
        fetch(`${config.API_URL}/admin/stores/${store.id}/working-hours`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ workingHours }),
        }),
        fetch(`${config.API_URL}/admin/stores/${store.id}/early-cutoffs`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ earlyCutoffs }),
        }),
      ]);
    } catch (err: any) {
      Logger.error('Failed to save schedule:', err);
      setSnackbar({ open: true, message: 'Failed to save schedule', severity: 'error' });
    } finally {
      setSavingSchedule(false);
    }
  };

  /** The Store Name pencil: renames the Radar zones in place, then the store and its stock rows. */
  const saveStoreName = async (next: string) => {
    if (!next) {
      setSnackbar({ open: true, message: 'Store name cannot be empty', severity: 'error' });
      throw new Error('empty');
    }
    if (next === storeName) return;
    const token = await getValidToken();
    const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/name`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: next }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      setSnackbar({ open: true, message: body?.message || 'Failed to rename the store', severity: 'error' });
      throw new Error('rename failed');
    }
    const view: StoreLocationView = await res.json();
    setLocationView(view);
    setStoreName(view?.name || next); // as saved (the server also collapses inner spaces)
    setSnackbar({ open: true, message: 'Store renamed: Radar zones and stock rows updated', severity: 'success' });
  };

  /** In-app / Courier Display Name pencils. Blank saves null = the store name. */
  const saveDisplayName = (field: 'inAppDisplayName' | 'courierDisplayName', label: string, apply: (v: string) => void) => async (next: string) => {
    const token = await getValidToken();
    const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: next || null }),
    });
    if (!res.ok) {
      setSnackbar({ open: true, message: `Failed to save ${label}`, severity: 'error' });
      throw new Error('save failed');
    }
    apply(next.replace(/\s+/g, ' ')); // as saved: the server collapses inner spaces
    setSnackbar({ open: true, message: `${label} updated`, severity: 'success' });
  };

  /** Pickup notes save on their own (they sit beside the Radar form, not the page Save). */
  const savePickupNotes = async () => {
    setSavingPickupNotes(true);
    try {
      const token = await getValidToken();
      const pickupNotes = settings.pickupNotes.trim() || null;
      const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pickupNotes }),
      });
      if (!res.ok) throw new Error('Failed to update');
      // Saved: the page Save no longer counts it as a change
      setInitialSettings((prev) => (prev ? { ...prev, pickupNotes: settings.pickupNotes } : prev));
      setSnackbar({ open: true, message: 'Pickup notes updated', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save pickup notes', severity: 'error' });
    } finally {
      setSavingPickupNotes(false);
    }
  };

  const handleSavePhone = async () => {
    setSavingPhone(true);

    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/stores/${store.id}/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phoneValue.trim() || null }),
      });

      if (!res.ok) throw new Error('Failed to update');
      setSnackbar({ open: true, message: 'Phone number updated', severity: 'success' });
      setIsEditingPhone(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save phone number', severity: 'error' });
    } finally {
      setSavingPhone(false);
    }
  };

  // Get display values (either custom or default)
  const getDisplayDeliveryCharge = () => settings.deliveryCharge ?? defaults.deliveryCharge;
  const getDisplayFreeDelivery = () => settings.freeDeliveryAmount ?? defaults.freeDeliveryAmount;
  const getDisplayMinCart = () => settings.minimumCartCheckout ?? defaults.minimumCartCheckout;
  const getDisplayServiceFee = () => settings.serviceFee ?? defaults.serviceFee;
  const getDisplayAlertSettings = () => settings.alertSettings ?? defaults.alertSettings;

  // Detect if there are unsaved changes
  const hasChanges = useCallback(() => {
    if (!initialSettings) return false;

    // Check per-section toggle changes
    if (useCustomPricing !== initialUseCustomPricing) return true;
    if (useCustomAlerts !== initialUseCustomAlerts) return true;

    // Check settings changes
    if (settings.isActive !== initialSettings.isActive) return true;
    if (settings.deliveryCharge !== initialSettings.deliveryCharge) return true;
    if (settings.freeDeliveryAmount !== initialSettings.freeDeliveryAmount) return true;
    if (settings.minimumCartCheckout !== initialSettings.minimumCartCheckout) return true;
    if (settings.serviceFee !== initialSettings.serviceFee) return true;
    if (settings.pickupNotes !== initialSettings.pickupNotes) return true;
    if (settings.enablePickingFlow !== initialSettings.enablePickingFlow) return true;
    if (settings.holidayRule !== initialSettings.holidayRule) return true;
    if (settings.holidayOpenTime !== initialSettings.holidayOpenTime) return true;
    if (settings.holidayCloseTime !== initialSettings.holidayCloseTime) return true;
    if (JSON.stringify(settings.alertSettings) !== JSON.stringify(initialSettings.alertSettings)) return true;

    // Check schedule changes
    if (useGlobalSchedule !== initialUseGlobalSchedule) return true;
    if (!useGlobalSchedule && JSON.stringify(schedule) !== JSON.stringify(initialSchedule)) return true;

    return false;
  }, [settings, initialSettings, useCustomPricing, initialUseCustomPricing, useCustomAlerts, initialUseCustomAlerts, useGlobalSchedule, initialUseGlobalSchedule, schedule, initialSchedule]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.error }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Store Info Summary */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: colors.bgElevated, borderRadius: 2, border: `1px solid ${colors.border}` }}>
        <Typography variant="overline" sx={{ color: colors.textMuted, display: 'block', mb: 2, letterSpacing: '0.1em' }}>
          Store Information
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <InlineEditField
              icon={<StoreIcon sx={{ fontSize: 20, color: colors.info }} />}
              label="Store Name"
              value={storeName}
              emptyText="Not set"
              helper={`Link ID ${store.externalId} · renames its Radar zones and stock rows too`}
              maxLength={100}
              onSave={saveStoreName}
              colors={colors}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneIcon sx={{ fontSize: 20, color: colors.success }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>Phone Number</Typography>
                {isEditingPhone ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <TextField
                      size="small"
                      value={phoneValue}
                      onChange={(e) => setPhoneValue(e.target.value)}
                      placeholder="e.g. +27 11 123 4567"
                      disabled={savingPhone}
                      autoFocus
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: colors.bgCard,
                          color: colors.textPrimary,
                          '& fieldset': { borderColor: colors.border },
                        },
                        '& .MuiInputBase-input': { py: 0.75, px: 1.5, fontSize: '0.875rem' },
                      }}
                    />
                    <IconButton size="small" onClick={handleSavePhone} disabled={savingPhone} sx={{ color: colors.success }}>
                      {savingPhone ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={() => setIsEditingPhone(false)} disabled={savingPhone} sx={{ color: colors.error }}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                      {store.phoneNumber || 'Not set'}
                    </Typography>
                    <IconButton size="small" onClick={() => { setPhoneValue(store.phoneNumber || ''); setIsEditingPhone(true); }} sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.info } }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LocationIcon sx={{ fontSize: 20, color: colors.warning }} />
              <Box>
                <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>Location</Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  {store.address ? [store.address.suburb, store.address.city].filter(Boolean).join(', ') : 'Not set'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <InlineEditField
              icon={<StoreIcon sx={{ fontSize: 20, color: colors.success }} />}
              label="In-app Display Name"
              value={inAppDisplayName}
              emptyText={`Not set (shows "${storeName}")`}
              placeholder={storeName}
              helper="Shown to customers in the app, next to OPEN"
              maxLength={100}
              onSave={saveDisplayName('inAppDisplayName', 'In-app Display Name', setInAppDisplayName)}
              colors={colors}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <InlineEditField
              icon={<DeliveryIcon sx={{ fontSize: 20, color: colors.warning }} />}
              label="Courier Display Name"
              value={courierDisplayName}
              emptyText={`Not set (shows "${storeName}")`}
              placeholder={storeName}
              helper="The business name Uber Direct drivers see at pickup"
              maxLength={100}
              onSave={saveDisplayName('courierDisplayName', 'Courier Display Name', setCourierDisplayName)}
              colors={colors}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Store Active Toggle */}
        <PlateToggle
          label={settings.isActive ? 'Store is Active' : 'Store is Inactive (Coming Soon)'}
          description={settings.isActive ? 'Accepting online orders' : 'Customers see "Coming Soon" — activate when ready to launch'}
          checked={settings.isActive}
          onChange={(checked) => setSettings((s) => ({ ...s, isActive: checked }))}
          colors={colors}
          status
        />
      </Paper>

      {/* Store Configuration Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor: colors.bgElevated,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${colors.info}15`,
                border: `1px solid ${colors.info}30`,
              }}
            >
              <TuneIcon sx={{ fontSize: 22, color: colors.info }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: colors.textPrimary }}>
                Store Configuration
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Each section can use system defaults or custom values
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Location & Routing Section */}
      <SectionCard
        icon={<LocationIcon />}
        title="Location & Routing"
        expanded={locationExpanded}
        onToggle={() => setLocationExpanded(!locationExpanded)}
        accentColor={colors.info}
        badge={
          locationView ? (
            <Chip
              label={locationView.onboarded ? locationView.location.suburb || 'Set up' : 'Not set up'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: locationView.onboarded ? `${colors.info}15` : `${colors.warning}15`,
                color: locationView.onboarded ? colors.info : colors.warning,
                border: `1px solid ${locationView.onboarded ? `${colors.info}30` : `${colors.warning}30`}`,
              }}
            />
          ) : undefined
        }
        colors={colors}
      >
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 2 }}>
          Saved to the store's Radar geofence, so Sync Stores keeps it.
        </Typography>
        <StoreLocationForm
          storeId={store.id}
          submitLabel="Save to Radar"
          onLoaded={setLocationView}
          onSaved={(view) => {
            setLocationView(view);
            fetchSettings();
          }}
        />

        {/* Pickup notes go to every courier: Uber Direct (pickup_notes) and Loop (its notes) */}
        <Box sx={{ mt: 3, p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1 }}>
            Pickup notes for couriers
          </Typography>
          <TextField
            multiline rows={2} fullWidth
            placeholder="e.g. Entrance 4, ask for the Vaperite counter..."
            value={settings.pickupNotes}
            onChange={(e) => setSettings((s) => ({ ...s, pickupNotes: e.target.value }))}
            helperText="Shown to every courier (Uber Direct and Loop) when collecting from this store"
            inputProps={{ maxLength: 500 }}
            sx={{
              '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
              '& .MuiFormHelperText-root': { color: colors.textMuted },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={savePickupNotes}
              disabled={savingPickupNotes || settings.pickupNotes === (initialSettings?.pickupNotes ?? '')}
              startIcon={savingPickupNotes ? <CircularProgress size={14} /> : <SaveIcon />}
            >
              Save pickup notes
            </Button>
          </Box>
        </Box>
      </SectionCard>

      {/* Pricing & Cart Section */}
      <SectionCard
        icon={<CartIcon />}
        title="Pricing & Cart"
        expanded={pricingExpanded}
        onToggle={() => setPricingExpanded(!pricingExpanded)}
        accentColor={colors.success}
        badge={
          <Chip
            label={useCustomPricing ? 'Custom' : 'Default'}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: useCustomPricing ? `${colors.error}15` : `${colors.info}15`,
              color: useCustomPricing ? colors.error : colors.info,
              border: `1px solid ${useCustomPricing ? `${colors.error}30` : `${colors.info}30`}`,
            }}
          />
        }
        colors={colors}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {useCustomPricing ? 'Using custom values for this store' : 'Using system defaults'}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={useCustomPricing}
                onChange={(e) => {
                  setUseCustomPricing(e.target.checked);
                  if (e.target.checked) {
                    setSettings(prev => ({
                      ...prev,
                      deliveryCharge: prev.deliveryCharge ?? defaults.deliveryCharge,
                      freeDeliveryAmount: prev.freeDeliveryAmount ?? defaults.freeDeliveryAmount,
                      minimumCartCheckout: prev.minimumCartCheckout ?? defaults.minimumCartCheckout,
                      serviceFee: prev.serviceFee ?? defaults.serviceFee,
                    }));
                  }
                }}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.error },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: `${colors.error}60` },
                }}
              />
            }
            label={<Typography variant="caption">Custom</Typography>}
          />
        </Box>
        {useCustomPricing ? (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1 }}>Delivery Charge</Typography>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={settings.deliveryCharge ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, deliveryCharge: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder={`Default: R${defaults.deliveryCharge}`}
                  InputProps={{ startAdornment: <Typography sx={{ color: colors.textSecondary, mr: 1 }}>R</Typography> }}
                  helperText={settings.deliveryCharge !== null ? 'Custom value' : `Leave empty to use default: R${defaults.deliveryCharge}`}
                  sx={{
                    '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, fontFamily: 'monospace', '& fieldset': { borderColor: colors.border } },
                    '& .MuiFormHelperText-root': { color: colors.textMuted },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1 }}>Free Delivery Amount</Typography>
                <TextField
                  type="number" size="small" fullWidth
                  value={settings.freeDeliveryAmount ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, freeDeliveryAmount: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder={defaults.freeDeliveryAmount ? `Default: R${defaults.freeDeliveryAmount}` : 'No free delivery'}
                  InputProps={{ startAdornment: <Typography sx={{ color: colors.textSecondary, mr: 1 }}>R</Typography> }}
                  helperText={settings.freeDeliveryAmount !== null ? 'Custom value' : (defaults.freeDeliveryAmount ? `Default: R${defaults.freeDeliveryAmount}` : 'No free delivery by default')}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, fontFamily: 'monospace', '& fieldset': { borderColor: colors.border } }, '& .MuiFormHelperText-root': { color: colors.textMuted } }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1 }}>Min Cart Checkout</Typography>
                <TextField
                  type="number" size="small" fullWidth
                  value={settings.minimumCartCheckout ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, minimumCartCheckout: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder={`Default: R${defaults.minimumCartCheckout}`}
                  InputProps={{ startAdornment: <Typography sx={{ color: colors.textSecondary, mr: 1 }}>R</Typography> }}
                  helperText={settings.minimumCartCheckout !== null ? 'Custom value' : `Default: R${defaults.minimumCartCheckout}`}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, fontFamily: 'monospace', '& fieldset': { borderColor: colors.border } }, '& .MuiFormHelperText-root': { color: colors.textMuted } }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1 }}>Service Fee</Typography>
                <TextField
                  type="number" size="small" fullWidth
                  value={settings.serviceFee ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, serviceFee: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder={`Default: R${defaults.serviceFee}`}
                  InputProps={{ startAdornment: <Typography sx={{ color: colors.textSecondary, mr: 1 }}>R</Typography> }}
                  helperText={settings.serviceFee !== null ? 'Custom value' : `Default: R${defaults.serviceFee}`}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, fontFamily: 'monospace', '& fieldset': { borderColor: colors.border } }, '& .MuiFormHelperText-root': { color: colors.textMuted } }}
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <SettingBadge icon={<DeliveryIcon sx={{ fontSize: 14 }} />} label="Delivery" value={`R${defaults.deliveryCharge}`} color={colors.success} colors={colors} />
            {defaults.freeDeliveryAmount && (
              <SettingBadge icon={<CartIcon sx={{ fontSize: 14 }} />} label="Free over" value={`R${defaults.freeDeliveryAmount}`} color={colors.success} colors={colors} />
            )}
            <SettingBadge icon={<CartIcon sx={{ fontSize: 14 }} />} label="Min cart" value={`R${defaults.minimumCartCheckout}`} color={colors.success} colors={colors} />
            <SettingBadge icon={<CartIcon sx={{ fontSize: 14 }} />} label="Service fee" value={`R${defaults.serviceFee}`} color={colors.success} colors={colors} />
          </Box>
        )}
      </SectionCard>

      {/* Order Operations Section */}
      <SectionCard
        icon={<OperationsIcon />}
        title="Order Operations"
        expanded={operationsExpanded}
        onToggle={() => setOperationsExpanded(!operationsExpanded)}
        accentColor={colors.warning}
        colors={colors}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* "Enable Picking Flow" removed (Jason, 2026-10-31): it only swapped the admin
              dash's Packed button for "Begin Picking"; the tablets never read it. The
              setting still loads and saves unchanged, so turning it back on is this toggle. */}

          {/* Public holiday trading.
              One setting covers every enabled holiday — a branch that shuts on
              Christmas shuts on Heritage Day — so this is a store property, not
              a per-date one. Dates themselves are enabled in Admin Tools →
              Public Holiday Manager. */}
          <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary }}>
                Public Holiday Trading
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 2 }}>
              What this store does on an enabled public holiday. Applied at 5am, ordinary
              hours restored at midnight.
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={settings.holidayRule}
              onChange={(_e, value) => {
                if (!value) return;
                setSettings((s) => ({
                  ...s,
                  holidayRule: value,
                  // Give 'custom' a usable window rather than an invalid empty one —
                  // the API rejects custom without both times.
                  holidayOpenTime: value === 'custom' ? (s.holidayOpenTime || '09:00:00') : null,
                  holidayCloseTime: value === 'custom' ? (s.holidayCloseTime || '17:00:00') : null,
                }));
              }}
              sx={{ mb: settings.holidayRule === 'custom' ? 2 : 0 }}
            >
              <ToggleButton value="closed">Closed</ToggleButton>
              <ToggleButton value="normal">Open normally</ToggleButton>
              <ToggleButton value="custom">Set hours</ToggleButton>
            </ToggleButtonGroup>

            {settings.holidayRule === 'custom' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Opens"
                  type="time"
                  size="small"
                  value={(settings.holidayOpenTime || '09:00:00').slice(0, 5)}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, holidayOpenTime: `${e.target.value}:00` }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Closes"
                  type="time"
                  size="small"
                  value={(settings.holidayCloseTime || '17:00:00').slice(0, 5)}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, holidayCloseTime: `${e.target.value}:00` }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}

            {settings.holidayRule === 'closed' && (
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 1.5 }}>
                This is the default for a store nobody has configured.
              </Typography>
            )}
          </Box>

          {SHOW_SLA && (
          <Box sx={{ p: 2, bgcolor: colors.bgDeepest, borderRadius: 2, border: `1px solid ${colors.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AlertIcon sx={{ fontSize: 18, color: colors.error }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary }}>SLA Alert Settings</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, mr: 1 }}>
                  {useCustomAlerts ? 'Custom' : 'Default'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomAlerts}
                      onChange={(e) => {
                        setUseCustomAlerts(e.target.checked);
                        if (e.target.checked) {
                          setSettings(prev => ({
                            ...prev,
                            alertSettings: prev.alertSettings ?? defaults.alertSettings,
                          }));
                        }
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.error },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: `${colors.error}60` },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            </Box>

            {useCustomAlerts ? (
              <>
                <PlateToggle
                  label="Enable SLA Alerts"
                  description="Notify when orders exceed time thresholds"
                  checked={getDisplayAlertSettings().alertsEnabled}
                  onChange={(checked) => {
                    setSettings((s) => ({
                      ...s,
                      alertSettings: { ...(s.alertSettings || defaults.alertSettings), alertsEnabled: checked }
                    }));
                  }}
                  colors={colors}
                />

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Picking SLA" type="number" size="small" fullWidth
                      value={getDisplayAlertSettings().pickingSlaMinutes}
                      onChange={(e) => setSettings((s) => ({
                        ...s,
                        alertSettings: { ...(s.alertSettings || defaults.alertSettings), pickingSlaMinutes: parseInt(e.target.value) || 1 }
                      }))}
                      disabled={!getDisplayAlertSettings().alertsEnabled}
                      InputProps={{ endAdornment: <Typography sx={{ color: colors.textMuted }}>min</Typography> }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } }, '& .MuiInputLabel-root': { color: colors.textMuted } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Packing SLA" type="number" size="small" fullWidth
                      value={getDisplayAlertSettings().packingSlaMinutes}
                      onChange={(e) => setSettings((s) => ({
                        ...s,
                        alertSettings: { ...(s.alertSettings || defaults.alertSettings), packingSlaMinutes: parseInt(e.target.value) || 1 }
                      }))}
                      disabled={!getDisplayAlertSettings().alertsEnabled}
                      InputProps={{ endAdornment: <Typography sx={{ color: colors.textMuted }}>min</Typography> }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } }, '& .MuiInputLabel-root': { color: colors.textMuted } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Pickup SLA" type="number" size="small" fullWidth
                      value={getDisplayAlertSettings().pickupSlaMinutes}
                      onChange={(e) => setSettings((s) => ({
                        ...s,
                        alertSettings: { ...(s.alertSettings || defaults.alertSettings), pickupSlaMinutes: parseInt(e.target.value) || 1 }
                      }))}
                      disabled={!getDisplayAlertSettings().alertsEnabled}
                      InputProps={{ endAdornment: <Typography sx={{ color: colors.textMuted }}>min</Typography> }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.bgCard, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } }, '& .MuiInputLabel-root': { color: colors.textMuted } }}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <SettingBadge
                  icon={<AlertIcon sx={{ fontSize: 14 }} />}
                  label="SLA Alerts"
                  value={defaults.alertSettings.alertsEnabled ? 'Enabled' : 'Disabled'}
                  color={defaults.alertSettings.alertsEnabled ? colors.success : colors.textMuted}
                  colors={colors}
                />
                {defaults.alertSettings.alertsEnabled && (
                  <>
                    <SettingBadge icon={<ClockIcon sx={{ fontSize: 14 }} />} label="Picking" value={`${defaults.alertSettings.pickingSlaMinutes}m`} color={colors.warning} colors={colors} />
                    <SettingBadge icon={<ClockIcon sx={{ fontSize: 14 }} />} label="Packing" value={`${defaults.alertSettings.packingSlaMinutes}m`} color={colors.warning} colors={colors} />
                    <SettingBadge icon={<ClockIcon sx={{ fontSize: 14 }} />} label="Pickup" value={`${defaults.alertSettings.pickupSlaMinutes}m`} color={colors.warning} colors={colors} />
                  </>
                )}
              </Box>
            )}
          </Box>
          )}
        </Box>
      </SectionCard>

      {/* Store Schedule */}
      <SectionCard
        icon={<ClockIcon />}
        title="Store Schedule"
        expanded={scheduleExpanded}
        onToggle={() => setScheduleExpanded(!scheduleExpanded)}
        accentColor={colors.info}
        badge={
          <Chip
            label={useGlobalSchedule ? 'Global defaults' : 'Custom hours'}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              bgcolor: `${useGlobalSchedule ? colors.info : colors.successGlow}15`,
              color: useGlobalSchedule ? colors.info : colors.successGlow,
            }}
          />
        }
        colors={colors}
      >
        {scheduleLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: colors.info }} />
          </Box>
        ) : (
          <SmartScheduleEditor
            schedule={schedule}
            globalSchedule={globalSchedule}
            useGlobalDefaults={useGlobalSchedule}
            onScheduleChange={setSchedule}
            onUseGlobalDefaultsChange={setUseGlobalSchedule}
            saving={savingSchedule}
            embedded
          />
        )}
      </SectionCard>

      {/* Save Button - only shown when there are changes */}
      <Collapse in={hasChanges()}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSaveAll}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              bgcolor: colors.error,
              color: mode === 'dark' ? '#000' : '#FFF',
              fontWeight: 600,
              '&:hover': { bgcolor: mode === 'dark' ? '#FF3366' : '#B71C1C' },
              '&.Mui-disabled': { bgcolor: colors.border, color: colors.textMuted },
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Collapse>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ bgcolor: colors.bgElevated, color: colors.textPrimary, '& .MuiAlert-icon': { color: snackbar.severity === 'success' ? colors.success : colors.error } }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ConfigTab;
